// ABOUTME: Hook for the quantity field with discriminated union support.
// Handles both 'quantity' (exact m³) and 'fullLoads' (1-3 trucks) modes.

import _ from 'lodash'
import {useMemo} from 'react'
import {useWatch} from 'react-hook-form'

import {useFieldConfigurationContext} from '../../configuration/useConfigurationContext'
import {useOrderProcessFormContext} from '../../form/useOrderProcessFormContext'

import {
  BaseQuantityReturn,
  QuantityByFullLoads,
  QuantityByValue,
  QuantitySelection,
  UseQuantityReturn
} from './types'

export const useQuantity = (): UseQuantityReturn => {
  const {
    control,
    setValue,
    trigger,
    formState: {defaultValues}
  } = useOrderProcessFormContext()
  const quantityConfiguration = useFieldConfigurationContext('quantity')
  const fullLoadConfiguration = useFieldConfigurationContext('fullLoad')

  const quantitySelection = useWatch({
    name: 'quantity',
    control
  }) as QuantitySelection

  const defaultQuantitySelection = defaultValues?.quantity as QuantitySelection | undefined
  const defaultQuantity =
    defaultQuantitySelection?.type === 'fullLoads'
      ? (defaultQuantitySelection as QuantityByFullLoads).minQuantity
      : (defaultQuantitySelection as QuantityByValue | undefined)?.volume

  const setType = (type: 'volume' | 'fullLoads') => {
    if (type === 'volume') {
      setValue('quantity', {
        type: 'volume',
        volume: defaultQuantity ?? quantityConfiguration.default ?? 0,
        quantityUom: quantitySelection.quantityUom
      })
    } else {
      if (!fullLoadConfiguration.isEnabled) {
        console.warn('Cannot switch to fullLoads mode: full load configuration is not enabled')
        return
      }
      const {defaultOption} = fullLoadConfiguration
      setValue('quantity', {
        type: 'fullLoads',
        fullLoads: defaultOption.numberOfLoads,
        minQuantity: defaultOption.minQuantity,
        maxQuantity: defaultOption.maxQuantity,
        quantityUom: quantitySelection.quantityUom
      })
    }
    trigger().catch((error) => {
      console.error('Error triggering validation:', error)
    })
  }

  const setVolume = async (newVolume: number) => {
    setValue('quantity', {
      ...quantitySelection,
      type: 'volume',
      volume: newVolume
    })
    await trigger()
  }

  const setFullLoads = (count: number) => {
    if (!fullLoadConfiguration.isEnabled) {
      console.warn('Cannot set full loads: full load configuration is not enabled')
      return
    }
    const option = fullLoadConfiguration.options.find((opt) => opt.numberOfLoads === count)
    if (!option) {
      console.warn(`Cannot set full loads: no option found for count ${count}`)
      return
    }
    setValue('quantity', {
      ...quantitySelection,
      type: 'fullLoads',
      fullLoads: count,
      minQuantity: option.minQuantity,
      maxQuantity: option.maxQuantity
    })
    trigger().catch((error) => {
      console.error('Error triggering validation:', error)
    })
  }

  const maxAutoConfirmable = quantityConfiguration?.maxAutoConfirmable
  const isMaxAutoConfirmQuantityExceeded = useMemo(() => {
    if (quantitySelection.type !== 'volume') return false
    if (_.isNil(maxAutoConfirmable)) return false
    return quantitySelection.volume > maxAutoConfirmable
  }, [quantitySelection, maxAutoConfirmable])

  const selectedFullLoadOption = useMemo(() => {
    if (quantitySelection.type !== 'fullLoads' || !fullLoadConfiguration.isEnabled) {
      return undefined
    }
    return fullLoadConfiguration.options.find(
      (opt) => opt.numberOfLoads === quantitySelection.fullLoads
    )
  }, [quantitySelection, fullLoadConfiguration])

  const fullLoadOptions = fullLoadConfiguration.isEnabled ? fullLoadConfiguration.options : []

  const base: BaseQuantityReturn = {
    quantitySelection,
    defaultQuantitySelection,
    defaultQuantity,
    setType,
    quantityConfiguration,
    isFullLoadEnabled: fullLoadConfiguration.isEnabled,
    fullLoadOptions
  }

  if (quantitySelection.type === 'fullLoads') {
    return {
      ...base,
      type: 'fullLoads',
      fullLoads: quantitySelection.fullLoads,
      setFullLoads,
      minQuantity: quantitySelection.minQuantity,
      maxQuantity: quantitySelection.maxQuantity,
      selectedFullLoadOption
    }
  }

  return {
    ...base,
    type: 'volume',
    volume: quantitySelection.volume,
    setVolume,
    isMaxAutoConfirmQuantityExceeded
  }
}
